"use client"

import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import {
  FileUp,
  Download,
  ZoomIn,
  ZoomOut,
  Grid3X3,
  Loader2,
  Trash2,
  Maximize,
  RotateCw,
  Copy,
  Undo2,
  Redo2,
  Keyboard,
  FlipHorizontal,
  FlipVertical,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
} from "lucide-react"
import type { ImageItem } from "./types"

interface WorkspaceToolbarProps {
  leftPanelCollapsed: boolean
  onToggleLeftPanel: () => void
  rightPanelCollapsed: boolean
  onToggleRightPanel: () => void
  inputProps: React.InputHTMLAttributes<HTMLInputElement>
  onOpenFilePicker: () => void
  canUndo: boolean
  canRedo: boolean
  onUndo: () => void
  onRedo: () => void
  canvasScale: number
  onZoomIn: () => void
  onZoomOut: () => void
  onResetZoom: () => void
  showGrid: boolean
  onToggleGrid: () => void
  isProcessing: boolean
  selectedItem: ImageItem | null
  onFitToPage: () => void
  onRotate90: () => void
  onFlipHorizontal: () => void
  onFlipVertical: () => void
  onDuplicateItem: (id: string) => void
  onDeleteItem: (id: string) => void
  onToggleShortcuts: () => void
  status: string
  uploadProgress: number
  totalImages: number
  onGeneratePdf: () => void
}

export function WorkspaceToolbar({
  leftPanelCollapsed,
  onToggleLeftPanel,
  rightPanelCollapsed,
  onToggleRightPanel,
  inputProps,
  onOpenFilePicker,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  canvasScale,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  showGrid,
  onToggleGrid,
  isProcessing,
  selectedItem,
  onFitToPage,
  onRotate90,
  onFlipHorizontal,
  onFlipVertical,
  onDuplicateItem,
  onDeleteItem,
  onToggleShortcuts,
  status,
  uploadProgress,
  totalImages,
  onGeneratePdf,
}: WorkspaceToolbarProps) {
  return (
    <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30 gap-2">
      <div className="flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onToggleLeftPanel}
            >
              {leftPanelCollapsed ? (
                <PanelLeftOpen className="h-4 w-4" />
              ) : (
                <PanelLeftClose className="h-4 w-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{leftPanelCollapsed ? "Show pages" : "Hide pages"}</TooltipContent>
        </Tooltip>

        <Separator orientation="vertical" className="h-6 mx-1" />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" size="sm" onClick={onOpenFilePicker} disabled={isProcessing}>
              <FileUp className="h-4 w-4 mr-1" />
              Add
            </Button>
          </TooltipTrigger>
          <TooltipContent>Upload images</TooltipContent>
        </Tooltip>
        <input {...inputProps} />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={onUndo}
              disabled={!canUndo || isProcessing}
            >
              <Undo2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Undo (Ctrl+Z)</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={onRedo}
              disabled={!canRedo || isProcessing}
            >
              <Redo2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Redo (Ctrl+Y)</TooltipContent>
        </Tooltip>

        <Separator orientation="vertical" className="h-6 mx-1" />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={onZoomOut}
              disabled={isProcessing}
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Zoom out (Ctrl+-)</TooltipContent>
        </Tooltip>

        <Button
          variant="ghost"
          size="sm"
          className="w-14 text-xs font-mono"
          onClick={onResetZoom}
          disabled={isProcessing}
        >
          {Math.round(canvasScale * 100)}%
        </Button>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={onZoomIn}
              disabled={isProcessing}
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Zoom in (Ctrl+=)</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={showGrid ? "secondary" : "outline"}
              size="icon"
              className="h-8 w-8"
              onClick={onToggleGrid}
              disabled={isProcessing}
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Toggle grid (G)</TooltipContent>
        </Tooltip>

        {selectedItem && (
          <>
            <Separator orientation="vertical" className="h-6 mx-1" />

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={onFitToPage}
                  disabled={isProcessing}
                >
                  <Maximize className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Fit to page (F)</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={onRotate90}
                  disabled={isProcessing}
                >
                  <RotateCw className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Rotate 90° (R)</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={selectedItem.flipX ? "secondary" : "outline"}
                  size="icon"
                  className="h-8 w-8"
                  onClick={onFlipHorizontal}
                  disabled={isProcessing}
                >
                  <FlipHorizontal className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Flip horizontal</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={selectedItem.flipY ? "secondary" : "outline"}
                  size="icon"
                  className="h-8 w-8"
                  onClick={onFlipVertical}
                  disabled={isProcessing}
                >
                  <FlipVertical className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Flip vertical</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onDuplicateItem(selectedItem.id)}
                  disabled={isProcessing}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Duplicate (Ctrl+D)</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => onDeleteItem(selectedItem.id)}
                  disabled={isProcessing}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete (Del)</TooltipContent>
            </Tooltip>
          </>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onToggleShortcuts}
            >
              <Keyboard className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Keyboard shortcuts (?)</TooltipContent>
        </Tooltip>

        {isProcessing && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {status === "creating-job" && "Preparing..."}
            {status === "uploading" && `Uploading ${Math.round(uploadProgress)}%`}
            {status === "processing" && "Generating PDF..."}
          </div>
        )}

        <Button onClick={onGeneratePdf} disabled={totalImages === 0 || isProcessing} className="gap-1.5">
          <Download className="h-4 w-4" />
          {isProcessing ? "Processing..." : "Download PDF"}
        </Button>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onToggleRightPanel}
            >
              {rightPanelCollapsed ? (
                <PanelRightOpen className="h-4 w-4" />
              ) : (
                <PanelRightClose className="h-4 w-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{rightPanelCollapsed ? "Show panel" : "Hide panel"}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  )
}
